import {
  MATCH_FILTER_CONDITION_ALL,
  MATCH_FILTER_CONDITION_AT_LEAST_ONE
} from 'Constants'
import {
  Chip,
  ChipIconInfo,
  ChipIconTimes,
  ChipIconWrapper,
  Flex,
  FlexItem,
  Grid,
  GridCell,
  Panel,
  Select,
  SELECT_VARIANT_WITH_CHECKBOX,
  SPACING_24,
  SpacingVertical,
  Switcher,
  Text,
  TEXT_VARIANT_BOLD,
  Title,
  TITLE_VARIANT_M,
  TooltipUtils
} from 'mailup-web-components'
import { initSegmentationCriteriaCallback } from 'Pages/Workflow/recoil/callbacks/callbacks'
import { segmentationCriteriaState } from 'Pages/Workflow/recoil/state/data'
import { modalOptionalFilterDetailState } from 'Pages/Workflow/recoil/state/modals'
import { step4State } from 'Pages/Workflow/recoil/state/step4'
import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilState, useSetRecoilState } from 'recoil'

const SegmentationBox = () => {
  const intl = useIntl()
  const [{ loading, loaded, groups, filters }, setCriteria] = useRecoilState(
    segmentationCriteriaState
  )
  const [{ Group, Filter, FiltersRule }, setWorkflowDetails] =
    useRecoilState(step4State)
  const initSegmentationCriteria = useRecoilCallback(
    initSegmentationCriteriaCallback,
    []
  )
  const setModal = useSetRecoilState(modalOptionalFilterDetailState)

  const withNoGroup = useMemo(() => {
    return [
      {
        value: 0,
        label: intl.formatMessage({ id: 'Load.Filters.NoGroupOption' })
      },
      ...groups
    ]
  }, [groups])

  const handleIncludeChange = (value) => {
    const included = value.map((e) => ({
      IdGroup: e,
      Action: true
    }))
    setWorkflowDetails((prev) => ({
      ...prev,
      Group: prev.Group.filter(
        (e) => !value.includes(e.IdGroup) && e.Action === false
      ).concat(included)
    }))
  }

  const handleExcludeChange = (value) => {
    const excluded = value.map((e) => ({
      IdGroup: e,
      Action: false
    }))
    setWorkflowDetails((prev) => ({
      ...prev,
      Group: prev.Group.filter(
        (e) => !value.includes(e.IdGroup) && e.Action === true
      ).concat(excluded)
    }))
  }

  const handleFiltersChange = (value) => {
    setWorkflowDetails((prev) => ({
      ...prev,
      Filter: value.map((e) => ({
        IdFilter: e
      }))
    }))
  }

  const handleChangeSwitcher = (value) => {
    setCriteria((prev) => ({
      ...prev,
      typeAppliedFilters: value
    }))
    setWorkflowDetails((prev) => ({
      ...prev,
      FiltersRule: value === 'All' ? 'AND' : 'OR'
    }))
  }

  /* eslint-disable react/prop-types */
  const SelectedItemComponent = (props) => {
    return (
      <Chip label={props.label} value={props.value}>
        <ChipIconWrapper
          onClick={() => setModal({ show: true, ...props, id: props.value })}
          data-mu-test='workflow-step4__apply-filter-details'
        >
          <ChipIconInfo />
        </ChipIconWrapper>
        <ChipIconWrapper
          onClick={(e) => props.onClick(props.value, e)}
          data-mu-test='workflow-step4__apply-filter-remove'
        >
          <ChipIconTimes />
        </ChipIconWrapper>
      </Chip>
    )
  }
  /* eslint-enable react/prop-types */

  const switcherOptions = [
    {
      label: intl.formatMessage({
        id: 'Load.SearchRecipients.filterAll'
      }),
      value: MATCH_FILTER_CONDITION_ALL,
      htmlAttrs: {
        'data-tip': intl.formatMessage({
          id: 'Load.SearchRecipients.filterAllTooltip'
        })
      }
    },
    {
      label: intl.formatMessage({
        id: 'Load.SearchRecipients.filterOne'
      }),
      value: MATCH_FILTER_CONDITION_AT_LEAST_ONE,
      htmlAttrs: {
        'data-tip': intl.formatMessage({
          id: 'Load.SearchRecipients.filterOneTooltip'
        })
      }
    }
  ]

  useEffect(() => {
    !loaded && initSegmentationCriteria()
  }, [loaded])

  useEffect(() => {
    TooltipUtils.rebuild()
  }, [Filter.length])

  return (
    <Panel>
      <Title variant={TITLE_VARIANT_M}>
        {intl.formatMessage({ id: 'Load.AutomationWorkflow.SegmentCondition' })}
      </Title>
      <SpacingVertical variant={SPACING_24} />
      <Flex flexDirection='column' rowGap='24px'>
        <Grid cols={12}>
          <GridCell span={2}>
            <FlexItem margin='8px 0 0 0'>
              <Text variant={TEXT_VARIANT_BOLD}>
                {intl.formatMessage({
                  id: 'Load.SearchRecipients.IncludeGroups'
                })}
              </Text>
            </FlexItem>
          </GridCell>
          <GridCell span={10}>
            <Select
              multiple
              searchable
              variant={SELECT_VARIANT_WITH_CHECKBOX}
              loading={loading}
              placeholder={intl.formatMessage({
                id: 'Load.SearchRecipients.IncludeGroupsPlaceholder'
              })}
              options={withNoGroup}
              value={Group.filter((e) => e.Action === true).map(
                (included) => included.IdGroup
              )}
              onChange={handleIncludeChange}
            />
          </GridCell>
        </Grid>
        <Grid cols={12}>
          <GridCell span={2}>
            <FlexItem margin='8px 0 0 0'>
              <Text variant={TEXT_VARIANT_BOLD}>
                {intl.formatMessage({
                  id: 'Load.SearchRecipients.ExcludeGroups'
                })}
              </Text>
            </FlexItem>
          </GridCell>
          <GridCell span={10}>
            <Select
              multiple
              searchable
              variant={SELECT_VARIANT_WITH_CHECKBOX}
              loading={loading}
              placeholder={intl.formatMessage({
                id: 'Load.SearchRecipients.ExcludeGroupsPlaceholder'
              })}
              options={groups}
              value={Group.filter((e) => e.Action === false).map(
                (excluded) => excluded.IdGroup
              )}
              onChange={handleExcludeChange}
            />
          </GridCell>
        </Grid>
        <Grid cols={12}>
          <GridCell span={2}>
            <FlexItem margin='8px 0 0 0'>
              <Text variant={TEXT_VARIANT_BOLD}>
                {intl.formatMessage({
                  id: 'Load.SearchRecipients.ApplyFilters'
                })}
              </Text>
            </FlexItem>
          </GridCell>
          <GridCell span={10}>
            <Flex
              width='100%'
              alignItems='flex-start'
              flexWrap='no-wrap'
              columnGap='32px'
            >
              {Filter.length > 1 && (
                <FlexItem width='auto' alignSelf='flex-start' margin='0 -8px'>
                  <Switcher
                    value={
                      FiltersRule === 'AND'
                        ? MATCH_FILTER_CONDITION_ALL
                        : MATCH_FILTER_CONDITION_AT_LEAST_ONE
                    }
                    onChange={handleChangeSwitcher}
                    options={switcherOptions}
                  />
                </FlexItem>
              )}
              <FlexItem width='100%'>
                <Select
                  multiple
                  searchable
                  variant={SELECT_VARIANT_WITH_CHECKBOX}
                  loading={loading}
                  placeholder={intl.formatMessage({
                    id: 'Load.SearchRecipients.ApplyFiltersPlaceholder'
                  })}
                  options={filters.options}
                  optionGroups={filters.optionGroups}
                  value={Filter.map((e) => e.IdFilter)}
                  onChange={handleFiltersChange}
                  SelectedItemComponent={SelectedItemComponent}
                  selectedFilters={Filter.map((e) => e.IdFilter)}
                />
              </FlexItem>
            </Flex>
          </GridCell>
        </Grid>
      </Flex>
    </Panel>
  )
}

export default SegmentationBox
